import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Button,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  MenuItem,
  Tooltip,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import {
  Search as SearchIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  FilterList as FilterIcon,
  Clear as ClearIcon,
  Person as PersonIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  CalendarToday as CalendarIcon,
  LocalShipping as DeliveryIcon,
  Restaurant as RestaurantIcon,
  Notifications as NotificationsIcon,
} from "@mui/icons-material";
import userApi from "../../api/users";
import { format, isValid, parseISO } from "date-fns";
import Layout from "../../components/layout/Layout";
import NotificationDialog from "./NotificationDialog";

interface User {
  _id: string;
  name: string;
  email: string;
  phoneNumber: string;
  firebaseUid?: string;
  expoPushToken?: string;
  addresses?: Array<{
    flatNumber: string;
    address: string;
    coordinates: {
      latitude: number;
      longitude: number;
    };
    postcode: string;
    city: string;
    state: string;
  }>;
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  subscriptionsCount: number;
  ordersCount: number;
}

// Helper function to safely format dates
const formatDate = (dateString: string) => {
  if (!dateString) return "N/A";

  try {
    const date = parseISO(dateString);
    return isValid(date) ? format(date, "MMM dd, yyyy") : "Invalid Date";
  } catch (error) {
    return "Invalid Date";
  }
};

// Helper function to format currency
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
  }).format(amount);
};

const Users: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);

  // Filters
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [isActive, setIsActive] = useState<boolean | null>(null);
  const [subscriptionFilter, setSubscriptionFilter] = useState<string>("");
  const [sortBy, setSortBy] = useState<string>("createdAt");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  // Notification dialog state
  const [notificationDialog, setNotificationDialog] = useState<{
    open: boolean;
    userId: string;
    userName: string;
  }>({
    open: false,
    userId: "",
    userName: "",
  });

  // Handle search input with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    fetchUsers();
  }, [page, rowsPerPage, debouncedSearch, isActive, subscriptionFilter, sortBy, sortOrder]);

  const fetchUsers = async () => {
    setLoading(true);

    try {
      const data = await userApi.getUsers({
        search: debouncedSearch,
        isActive: isActive === null ? undefined : isActive,
        subscriptionFilter: subscriptionFilter || undefined,
        page: page + 1,
        limit: rowsPerPage,
        sortBy,
        sortOrder,
      });

      console.log("Users data:", data); // Debug log
      console.log("Users array:", data.users);
      console.log("Pagination:", data.pagination);
      setUsers(data.users);
      setTotal(data.pagination.total);
    } catch (err: any) {
      console.error("Error fetching users:", err);
      setError(err.response?.data?.message || "Error fetching users");
    } finally {
      setLoading(false);
    }
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleResetFilters = () => {
    setSearchTerm("");
    setDebouncedSearch("");
    setIsActive(null);
    setSubscriptionFilter("");
    setSortBy("createdAt");
    setSortOrder("desc");
  };

  const handleOpenNotificationDialog = (userId: string, userName: string) => {
    setNotificationDialog({
      open: true,
      userId,
      userName,
    });
  };

  const handleCloseNotificationDialog = () => {
    setNotificationDialog({
      open: false,
      userId: "",
      userName: "",
    });
  };

  const getStatusColor = (isActive: boolean | undefined) => {
    // if (isActive === undefined) return "default";
    return "success";
  };

  const getStatusLabel = (isActive: boolean | undefined) => {
    // if (isActive === undefined) return "Unknown";
    return "Active";
  };

  const filteredUsers = users.filter(user =>
    searchTerm === "" ||
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.phoneNumber?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <Layout>
      <Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
          <Typography variant="h4" gutterBottom>
            Users
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchUsers}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Paper sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <FilterIcon sx={{ mr: 1 }} />
            <Typography variant="h6">Filters</Typography>
          </Box>
          <Box sx={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 2 }}>
            <TextField
              label="Search"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
              size="small"
              placeholder="Search by name, email or phone"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end">
                      <SearchIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <TextField
              select
              label="Status"
              value={isActive === null ? "" : isActive ? "active" : "inactive"}
              onChange={(e) => {
                if (e.target.value === "") {
                  setIsActive(null);
                } else {
                  setIsActive(e.target.value === "active");
                }
              }}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Users</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
            </TextField>
            <TextField
              select
              label="Subscription Filter"
              value={subscriptionFilter}
              onChange={(e) => setSubscriptionFilter(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="">All Subscriptions</MenuItem>
              <MenuItem value="at_least_one">At least 1 active/completed sub</MenuItem>
              <MenuItem value="more_than_one">More than 1 active/completed sub</MenuItem>
            </TextField>
            <TextField
              select
              label="Sort By"
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              fullWidth
              size="small"
            >
              <MenuItem value="createdAt">Join Date</MenuItem>
              <MenuItem value="name">Name</MenuItem>
              <MenuItem value="email">Email</MenuItem>
              <MenuItem value="subscriptionsCount">Subscriptions</MenuItem>
              <MenuItem value="ordersCount">Orders</MenuItem>
            </TextField>
            <TextField
              select
              label="Sort Order"
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value as "asc" | "desc")}
              fullWidth
              size="small"
            >
              <MenuItem value="desc">Descending</MenuItem>
              <MenuItem value="asc">Ascending</MenuItem>
            </TextField>
            <Box sx={{ display: "flex", alignItems: "center" }}>
              <Button
                variant="outlined"
                onClick={handleResetFilters}
                startIcon={<ClearIcon />}
                fullWidth
              >
                Clear Filters
              </Button>
            </Box>
          </Box>
        </Paper>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Users Table */}
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User Info</TableCell>
                <TableCell>Contact</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Activity</TableCell>
                <TableCell>Join Date</TableCell>
                <TableCell>Last Updated</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : error ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    Error: {error}
                  </TableCell>
                </TableRow>
              ) : filteredUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center">
                    No users found
                  </TableCell>
                </TableRow>
              ) : (
                filteredUsers.map((user) => (
                  <TableRow key={user._id} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="bold">
                          {user.name || "N/A"}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          ID: {user._id}
                        </Typography>
                        {user.firebaseUid && (
                          <Typography variant="caption" display="block" color="text.secondary">
                            Firebase: {user.firebaseUid.slice(-8)}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2">
                          {user.email || "N/A"}
                        </Typography>
                        <Typography variant="body2">
                          {user.phoneNumber || "N/A"}
                        </Typography>
                        {user.expoPushToken && (
                          <Typography variant="caption" color="success.main">
                            Push notifications enabled
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label="Active"
                        color="success"
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.5 }}>
                          <RestaurantIcon fontSize="small" color="primary" />
                          <Typography variant="body2">
                            {user.subscriptionsCount} subscriptions
                          </Typography>
                        </Box>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                          <DeliveryIcon fontSize="small" color="secondary" />
                          <Typography variant="body2">
                            {user.ordersCount} orders
                          </Typography>
                        </Box>
                        {user.addresses && user.addresses.length > 0 && (
                          <Typography variant="caption" color="text.secondary">
                            {user.addresses.length} address{user.addresses.length > 1 ? 'es' : ''}
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {formatDate(user.updatedAt)}
                      </Typography>
                    </TableCell>
                                          <TableCell>
                        <Box sx={{ display: "flex", gap: 0.5 }}>
                          <Tooltip title="View Details">
                            <IconButton
                              component={Link}
                              to={`/users/${user._id}`}
                              size="small"
                              color="primary"
                            >
                              <ViewIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Send Notification">
                            <IconButton
                              onClick={() => handleOpenNotificationDialog(user._id, user.name || "User")}
                              size="small"
                              color="secondary"
                            >
                              <NotificationsIcon />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={total}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </TableContainer>

        {/* Notification Dialog */}
        <NotificationDialog
          open={notificationDialog.open}
          onClose={handleCloseNotificationDialog}
          userId={notificationDialog.userId}
          userName={notificationDialog.userName}
        />
      </Box>
    </Layout>
  );
};

export default Users;
